import { MapState } from './state.js';
import {
	CITY_ZOOM_THRESHOLD,
	DEFAULT_LORE_HTML,
	BOUNDARY_STYLE,
	BOUNDARY_HALO_STYLE,
	BOUNDARY_HANDLE_PANE
} from './constants.js';
import { applyPanelMixins } from './mixins/panel.js';
import { applySearchMixins } from './mixins/search.js';
import { applyContentMixins } from './mixins/content.js';
import { applyMarkerMixins } from './mixins/markers.js';
import { applyModeMixins } from './mixins/mode.js';
import { applyBoundaryMixins } from './mixins/boundaries.js';
import { applyConstellationMixins } from './mixins/constellations.js';

/**
 * Primary application controller for the interactive map.
 * Handles UI initialization, Leaflet map configuration,
 * and coordinates the various feature mixins.
 */
export default class MapApp extends MapState {
	/**
	 * Create a new MapApp instance with default styling values.
	 */
	constructor() {
		super();
		this.cityZoomThreshold = CITY_ZOOM_THRESHOLD;
		this.defaultLoreHtml = DEFAULT_LORE_HTML;
		this.boundaryStyle = { ...BOUNDARY_STYLE };
		this.boundaryHaloStyle = { ...BOUNDARY_HALO_STYLE };
		this.boundaryHandlePane = BOUNDARY_HANDLE_PANE;
	}

	/**
	 * Wire up the UI shell, create the Leaflet map, attach layers/mixins,
	 * and kick off the initial data loads for locations and constellations.
	 */
	initialize() {
		this.infoPanel = document.getElementById('info_panel');
		this.infoPanelShell = this.infoPanel
			? this.infoPanel.closest('.info-panel-shell')
			: document.querySelector('.info-panel-shell');
		this.lorePanelEl = this.infoPanel
			? this.infoPanel.querySelector('.info-panel__inner')
			: null;
		this.infoToggleButton = this.infoPanelShell
			? this.infoPanelShell.querySelector('.info-panel__toggle')
			: null;
		this.lorePanelEl = document.getElementById('lore_panel');
		this.searchPanelEl = document.getElementById('search_panel');
		this.searchInputEl = document.getElementById('info_search');
		this.searchResultsEl = document.getElementById('info_search_results');

		const tabNodeList = this.infoPanel
			? this.infoPanel.querySelectorAll('.info-panel__tab')
			: null;
		if (tabNodeList && tabNodeList.length) {
			this.tabButtons = Array.prototype.slice.call(tabNodeList);
			this.tabButtons.forEach((button) => {
				button.addEventListener('click', () => {
					this.activatePanel(button.getAttribute('data-panel'));
				});
			});
		}

		if (this.infoPanel) {
			this.infoPanel.addEventListener('click', (event) =>
				this.onInfoPanelClick(event)
			);
		}

		if (this.infoToggleButton) {
			this.infoToggleButton.addEventListener('click', (event) =>
				this.onToggleClick(event)
			);
		}

		this.initializePanelResize();

		if (this.searchInputEl) {
			this.searchInputEl.addEventListener('input', () => this.onSearchInputChange());
			this.searchInputEl.addEventListener('keydown', (event) =>
				this.onSearchInputKeydown(event)
			);
		}

		if (this.searchResultsEl) {
			this.searchResultsEl.addEventListener('click', (event) =>
				this.onSearchResultsClick(event)
			);
		}

		if (typeof globalThis.addEventListener === 'function') {
			globalThis.addEventListener('hashchange', () => this.onHashChange());
			globalThis.addEventListener('popstate', () => this.onHashChange());
		}

		this.activatePanel('lore');
		this.collapsePanel();
		this.initializeCityCoordinateIndex();

		this.map = globalThis.L.map('map_canvas', {
			center: [0, -54.034165],
			zoom: 3,
			minZoom: 1,
			maxZoom: 6,
			zoomControl: true,
			scrollWheelZoom: true,
			attributionControl: false
		});

		if (this.map) {
			this.defaultZoomLevel = this.map.getZoom();
			if (this.countryMarkerPane && typeof this.map.createPane === 'function') {
				this.map.createPane(this.countryMarkerPane);
				const pane = this.map.getPane(this.countryMarkerPane);
				if (pane) {
					pane.style.zIndex = '700';
					pane.style.pointerEvents = 'none';
				}
			}
		}

		const primaryTileBase = '/map/tiles/{z}/{x}/{y}.jpg';

		this.baseTileLayer = globalThis.L.tileLayer(primaryTileBase, {
			minZoom: 1,
			maxZoom: 6,
			tileSize: 256,
			noWrap: false,
			continuousWorld: true,
			keepBuffer: 2
		});
		if (this.baseTileLayer) {
			this.baseTileLayer.addTo(this.map);
			this.baseTileOpacity =
				typeof this.baseTileLayer.options.opacity === 'number'
					? this.baseTileLayer.options.opacity
					: 1;
		}

		if (this.countryLayer) {
			this.countryLayer.addTo(this.map);
		}
		if (this.cityLayer) {
			this.cityLayer.addTo(this.map);
		}

		this.setupStarLineLayer();
		this.setupSyntheticStarLayer();

		this.map.createPane('boundaries');
		const boundaryPane = this.map.getPane('boundaries');
		if (boundaryPane) {
			boundaryPane.style.zIndex = '350';
			boundaryPane.style.pointerEvents = 'none';
		}

		this.boundaryHaloLayer = globalThis.L.geoJSON(null, {
			style: () => this.getBoundaryHaloStyle(),
			pane: 'boundaries',
			interactive: false
		});
		this.boundaryCoreLayer = globalThis.L.geoJSON(null, {
			style: () => this.getBoundaryStyle(),
			pane: 'boundaries',
			interactive: false
		});
		this.boundaryLayer = globalThis.L.layerGroup([], { pane: 'boundaries' });
		this.boundaryLayer.addLayer(this.boundaryHaloLayer);
		this.boundaryLayer.addLayer(this.boundaryCoreLayer);
		this.boundaryLayer.addData = (geojson) => {
			this.boundaryHaloLayer.addData(geojson);
			this.boundaryCoreLayer.addData(geojson);
		};
		this.boundaryLayer.clearLayers = () => {
			this.boundaryHaloLayer.clearLayers();
			this.boundaryCoreLayer.clearLayers();
		};
		this.boundaryLayer.addTo(this.map);

		this.map.createPane(this.getBoundaryHandlePane());
		const handlePane = this.map.getPane(this.getBoundaryHandlePane());
		if (handlePane) {
			handlePane.style.zIndex = '360';
			handlePane.style.pointerEvents = 'auto';
		}
		this.boundaryControlLayer = globalThis.L.featureGroup([], {
			pane: this.getBoundaryHandlePane()
		}).addTo(this.map);

		this.addCountryMarkers();
		this.buildSearchIndex();
		this.renderSearchResults('');
		this.updateSearchSelection();
		this.scheduleCityMarkers();
		if (!Array.isArray(globalThis.cities) || globalThis.cities.length === 0) {
			this.schedulePanelReveal(450);
		}

		this.loadStarData();

		this.map.on('zoomend', () => this.onZoomChange());

		setTimeout(() => {
			this.schedulePanelReveal(640);
		}, 3000);

		this.onZoomChange();
		this.applyAnchorFromLocation(true);
		this.initializeModeManager();
	}

	/**
	 * Build the runtime GeoJSON style object for national boundaries.
	 * @returns {object} Leaflet style definition.
	 */
	getBoundaryStyle() {
		return {
			color: this.boundaryStyle.color,
			weight: this.boundaryStyle.weight,
			opacity: this.boundaryStyle.opacity,
			fillColor: this.boundaryStyle.fillColor,
			fillOpacity: this.boundaryStyle.fillOpacity,
			lineJoin: this.boundaryStyle.lineJoin,
			lineCap: this.boundaryStyle.lineCap,
			className: this.boundaryStyle.className
		};
	}

	getBoundaryHaloStyle() {
		return {
			color: this.boundaryHaloStyle.color,
			weight: this.boundaryHaloStyle.weight,
			opacity: this.boundaryHaloStyle.opacity,
			fillColor: this.boundaryHaloStyle.fillColor,
			fillOpacity: this.boundaryHaloStyle.fillOpacity,
			lineJoin: this.boundaryHaloStyle.lineJoin,
			lineCap: this.boundaryHaloStyle.lineCap,
			className: this.boundaryHaloStyle.className
		};
	}

	/**
	 * Return the pane name used for boundary editing handles.
	 * @returns {string} Pane identifier.
	 */
	getBoundaryHandlePane() {
		return this.boundaryHandlePane;
	}

}

applyPanelMixins(MapApp);
applySearchMixins(MapApp);
applyContentMixins(MapApp);
applyMarkerMixins(MapApp);
applyModeMixins(MapApp);
applyBoundaryMixins(MapApp);
applyConstellationMixins(MapApp);
